import { pdf } from '@react-pdf/renderer';
import { ModalContext } from 'app/app';
import axios from 'axios';
import { IconedButton } from 'components/IconedButton';
import {
  Animate,
  ViewContext,
} from 'components/lib';
import Certificate from 'components/modals/CertificatesModal/certificate';
import DateUtils from 'helpers/dateUtils';
import { useContext, useEffect, useState } from 'react';
import styles from './styles.module.scss';
import ResponsiveTable from 'components/ResponsiveTable';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { certificateColumns } from './columns';
import CustomLoader from 'components/CustomLoader';

export function Certificates() {
  const [data, setData] = useState([]);
  const [user, setUser] = useState({});
  const [loading, setLoading] = useState(false);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const { setModal } = useContext(ModalContext);
  const context = useContext(ViewContext);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });

  const format = new DateUtils();

  async function listCertificates(
    page,
    pageSize,
    sortBy = 'createdAt',
    sortOrder = 'desc',
    search = ''
  ) {
    setLoading(true);

    const user = await axios.get('/api/user');
    setUser(user.data.data);

    await axios
      .get(`/api/certificate`, {
        params: {
          page,
          pageSize,
          search,
          sortBy,
          sortOrder
        }
      })
      .then(async (res) => {
        setLoading(false);
        const tempData = [];
        const timezone = await format.getTimezone();

        for (const value of res.data.data.data) {
          const formattedData = {
            id: value.id,
            'Company Name': value.company_name,
            'Document Type': value.type,
            link: value.link,
            submission_number: value.submission_number,
            contact: value.contact,
            certificate_holder: value.certificate_holder,
            'Date Uploaded': format.timestamp(
              value.createdAt,
              timezone
            ),
            'Refreshed at':
              value.createdAt === value.updatedAt
                ? ''
                : format.timestamp(value.updatedAt, timezone),
            'Uploaded By': value.uploaded_by,
            handleRefresh: async (row) => {
              await refreshCertificate(row);
            },
            handleDownload: (row) => {
              window.open(row.link);
            }
          };
          tempData.push(formattedData);
        }

        setData(tempData);
        setPagination(prev => ({
          ...prev,
          totalItems: res.data.data.rows,
          totalPages: res.data.data.pages_count,
        }));
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
        context.notification.show(
          'Failed to load certificates data',
          'error',
          true,
          'toast',
          'x-circle'
        );
      });
  }

  async function refreshCertificate(data) {
    setModal({
      name: 'success',
      data: {
        title: 'Updating this certificate',
        description:
          "Wait some minutes to our system finish certificate's update.",
      },
    });

    await axios
      .get(`/api/quote/info/${data.submission_number}`)
      .then(async (info) => {
        const producer = await axios.get('/api/user/' + user.id);
        const agency = await axios.get('/api/account');

        const pdfBlob = await pdf(
          <Certificate
            data={info.data.data}
            customerHolder={data.certificate_holder}
            signature={user.signature}
            contact={data.contact}
            agency={agency.data.data}
            producer={producer.data.data.name}
          />
        ).toBlob();

        const formData = new FormData();
        formData.append('file', pdfBlob, `certificate.pdf`);

        await axios
          .post('/api/certificate/file', formData)
          .then(async (res) => {
            await axios
              .patch('/api/certificate/' + data.id, {
                link: res.data.link,
              })
              .then(() => {
                setModal({});
                setLoading(false);

                window.open(res.data.link);

                const currentPage = pagination.pageIndex + 1;
                const currentSort = sorting.length > 0
                  ? {
                    field: sorting[0].id, order: sorting[0].desc
                      ? 'desc'
                      : 'asc'
                  }
                  : { field: 'createdAt', order: 'desc' };

                listCertificates(
                  currentPage,
                  pagination.pageSize,
                  currentSort.field,
                  currentSort.order,
                  globalFilter
                );

                context.notification.show(
                  'Certificate refreshed',
                  'success',
                  true,
                  'toast',
                  'check-circle'
                );
              })
              .catch(() => {
                setLoading(false);
                setModal({});

                context.notification.show(
                  'Error on create certificate',
                  'error',
                  true,
                  'toast',
                  'x-circle'
                );
              });
          })
          .catch(() => {
            setLoading(false);
            setModal({});

            context.notification.show(
              'Error on upload certificate to server',
              'error',
              true,
              'toast',
              'x-circle'
            );
          });
      })
      .catch(() => {
        setLoading(false);
        setModal({});

        context.notification.show(
          'Failed to load certificate details.',
          'error',
          true,
          'toast',
          'x-circle'
        );
      });
  }

  useEffect(() => {
    let currentSortField = 'createdAt';
    let currentSortDirection = 'desc';

    if (sorting.length > 0) {
      currentSortField = sorting[0].id;
      currentSortDirection = sorting[0].desc ? 'desc' : 'asc';
    }

    setIsSearching(globalFilter !== '');

    listCertificates(
      pagination.pageIndex + 1,
      pagination.pageSize,
      currentSortField,
      currentSortDirection,
      globalFilter
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    globalFilter,
    columnFilters
  ]);

  const table = useReactTable({
    data,
    columns: certificateColumns,
    state: {
      sorting,
      globalFilter,
      columnFilters,
      rowSelection,
      pagination,
    },
    initialState: {
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
    },
    // Pagination
    onPaginationChange: setPagination,
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    // Column
    onColumnFiltersChange: setColumnFilters,
    // Row
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    // Sort and Filter
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <Animate type="pop">
      <div>
        <div className="flex w-full items-center justify-end mb-4">
          <IconedButton
            size="180px"
            color="#d32425"
            textColor="white"
            icon="plus-circle"
            text="Create certificate"
            marginRight
            action={() =>
              setModal({
                name: 'certificates_modal',
                action: () => {
                  const currentPage = pagination.pageIndex + 1;
                  const currentSort = sorting.length > 0
                    ? { field: sorting[0].id, order: sorting[0].desc ? 'desc' : 'asc' }
                    : { field: 'createdAt', order: 'desc' };

                  listCertificates(
                    currentPage,
                    pagination.pageSize,
                    currentSort.field,
                    currentSort.order,
                    globalFilter
                  );
                },
              })
            }
          />
        </div>

        {loading && data.length === 0 ? (
          <div className="flex items-center justify-center h-96">
            <CustomLoader />
          </div>
        ) : data.length > 0 ? (
          <ResponsiveTable
            table={table}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            loading={loading}
            hasFooter={true}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
          />
        ) : isSearching ? (
          <div className={styles.centeredImage}>
            <div className={styles.text}>
              No certificates found matching your search criteria. <br />
              Try adjusting your search or clear it to see all certificates.
            </div>
            <div style={{ marginTop: 20 }}>
              <IconedButton
                size="180px"
                color="#4a5568"
                textColor="white"
                icon="search"
                text="Clear search"
                marginRight
                action={() => setGlobalFilter("")}
              />
            </div>
          </div>
        ) : (
          <div className={styles.centeredImage}>
            <div className={styles.text}>
              Nothing here yet! <br /> To add a new certificate,
              click in button down below:
            </div>
            <div style={{ marginTop: 20 }}>
              <IconedButton
                size="180px"
                color="#d32425"
                textColor="white"
                icon="plus-circle"
                text="Create certificate"
                marginRight
                action={() =>
                  setModal({
                    name: 'certificates_modal',
                    action: () => {
                      const currentPage = pagination.pageIndex + 1;
                      const currentSort = sorting.length > 0
                        ? {
                          field: sorting[0].id, order: sorting[0].desc
                            ? 'desc'
                            : 'asc'
                        }
                        : { field: 'createdAt', order: 'desc' };

                      listCertificates(
                        currentPage,
                        pagination.pageSize,
                        currentSort.field,
                        currentSort.order,
                        globalFilter
                      );
                    },
                  })
                }
              />
            </div>
          </div>
        )}
      </div>
    </Animate>
  );
}
